import React from 'react';
import { Field } from 'react-final-form';
import FormGroup, { FormGroupProps } from './FormGroup';
import { generateField } from './generator';
import { FormSchemaEntry } from './types';
import { FormContext } from './context';
import { AnyType } from '../Table';

interface FormFieldProps extends FormGroupProps {
  name: string;
  labelClassName?: string;
  vertical?: boolean;
  inline?: boolean;
  center?: boolean;
  disabled?: boolean; // force this field to be disabled
  extraProps?: object;
  //eslint-disable-next-line
  format?(value: any, name: string): any;
  //eslint-disable-next-line
  parse?(value: any, name: string): any;
  onChange?(value: AnyType): void;
}

/**
 * GIK FormField
 *
 * This component makes it easier to work with react-final-form and a form schema.
 * it only needs the name of the form entry that should be rendered, details on how to render
 * the form field is extracted from the schema.
 */
export default function FormField({
  name,
  vertical, // deliberately set to null instead of false so we can determine if the user did not use this property
  labelClassName,
  inline,
  center,
  disabled,
  extraProps,
  format,
  parse,
  onChange,
  ...otherProps
}: FormFieldProps): React.ReactElement {
  const context = FormContext.useContainer();

  const schemaEntry: FormSchemaEntry = context.schema ? context.schema.find(entry => entry.name === name) : null;

  if (!schemaEntry) return <div>{name} not found</div>;

  let isVertical = vertical === undefined || vertical === true;

  if (vertical === undefined) {
    isVertical = context.vertical;
  }

  const fieldProps = {
    /**
     * react-final-form needs this to be set to checkbox for checkbox fields,
     */
    type: schemaEntry.type === 'checkbox' ? 'checkbox' : null,
    formatOnBlur: true,
    //eslint-disable-next-line
    format: (value: any, name: string) => {
      // apply global formatting first
      if (typeof value === 'string') value = value?.trim();

      // apply custom formatting if provided through props
      if (format) value = format(value, name);

      // TODO: apply formatting if specified in schema

      // should not return null or undefined, see https://final-form.org/docs/react-final-form/types/FieldProps#format
      return value;
    },
    //eslint-disable-next-line
    parse: (value: any, name: string) => {
      // TODO: apply parsing if specified in schema

      // apply custom parsing if provided through props
      if (parse) value = parse(value, name);

      // should not return null or undefined, see https://final-form.org/docs/react-final-form/types/FieldProps#parse
      return value;
    },
  };

  return (
    <Field name={name} {...fieldProps}>
      {({ input, meta: fieldState }) => {
        input.disabled = disabled;
        return (
          <FormGroup
            label={schemaEntry.label}
            center={center}
            labelClassName={labelClassName || schemaEntry.labelClassName}
            required={schemaEntry.requiredIndicator}
            optional={schemaEntry.optional}
            vertical={isVertical}
            fieldWidth={schemaEntry.width}
            fieldType={schemaEntry.type}
            fieldName={schemaEntry.name}
            inline={inline}
            fieldState={fieldState}
            {...otherProps}
          >
            {generateField({
              entry: schemaEntry,
              formFieldProps: input,
              extraProps,
              disabled,
              fieldState,
              onChange,
            })}
          </FormGroup>
        );
      }}
    </Field>
  );
}
